/**
 * The trace phase for a live message, shared by the native and web activity
 * traces. `resolveTracePhase` needs to know how much answer text was already
 * on screen when the last tool result landed; this hook carries that across
 * renders (`advanceTracePhase`) so "Writing…" holds from the result until new
 * words arrive.
 */
import { useRef } from 'react';
import {
  advanceTracePhase,
  INITIAL_TRACE_PHASE_MEMORY,
  type TracePart,
  type TracePhase,
} from './activity-trace-model';

export function useTracePhase(
  parts: readonly TracePart[],
  isRunning: boolean,
  hiddenTools: ReadonlySet<string>,
): TracePhase {
  const memory = useRef(INITIAL_TRACE_PHASE_MEMORY);
  // Advanced during render on purpose: the phase of this render depends on
  // the previous one, and the update is idempotent for the same parts.
  const next = advanceTracePhase(memory.current, parts, isRunning, hiddenTools);
  memory.current = next.memory;
  return next.phase;
}
